
import { v4 as uuidv4 } from 'uuid';

export interface FalJob {
    id: string;
    provider: 'fal';
    engine: string;
    preset: string;
    params: any;
    startTime: number;
    endTime?: number;
    durationMs?: number;
    status: 'queued' | 'requesting' | 'polling' | 'downloading' | 'success' | 'error';
    request: {
        endpoint: string;
        payload: any;
    };
    response?: {
        raw: any;
        outputUrl?: string;
    };
    error?: {
        message: string;
        stack?: string;
        code?: number;
    };
    logs: FalLog[];
}

export interface FalLog {
    timestamp: number;
    tag: string;
    data: any;
}

type Listener = (jobs: FalJob[]) => void;

class FalDebugServiceImpl {
    private jobs: FalJob[] = [];
    private listeners: Listener[] = [];
    private isEnabled = typeof window !== 'undefined' && (
        window.location.hostname === 'localhost' ||
        window.location.search.includes('debug=true') ||
        process.env.NEXT_PUBLIC_DEBUG_PROOF_MODE === 'true'
    );

    constructor() {
        // Load from local storage if needed? For now in-memory is safer strictly for session proof.
    }

    getJobs() {
        return this.jobs;
    }

    toggleDebug(enabled: boolean) {
        this.isEnabled = enabled;
    }

    isDebugMode() {
        return this.isEnabled;
    }

    subscribe(listener: Listener) {
        this.listeners.push(listener);
        listener([...this.jobs]);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    private notify() {
        this.listeners.forEach(l => l([...this.jobs]));
    }

    // === LOGGING API ===

    startJob(engine: string, preset: string, params: any): string {
        const id = uuidv4();
        const job: FalJob = {
            id,
            provider: 'fal',
            engine,
            preset,
            params,
            startTime: Date.now(),
            status: 'queued',
            request: { endpoint: '', payload: {} }, // Will be filled in logRequest
            logs: []
        };
        this.jobs.unshift(job); // Newest first
        this.log(id, 'JOB_START', { engine, preset });
        this.notify();
        return id;
    }

    logRequest(jobId: string, endpoint: string, payload: any) {
        const job = this.jobs.find(j => j.id === jobId);
        if (!job) return;
        job.request = { endpoint, payload };
        job.status = 'requesting';
        this.log(jobId, 'REQUEST', { endpoint, payloadSummary: Object.keys(payload) });
        this.notify();
    }

    logStatus(jobId: string, status: 'polling' | 'downloading') {
        const job = this.jobs.find(j => j.id === jobId);
        if (!job) return;
        job.status = status;
        this.notify();
    }

    logResponse(jobId: string, rawResponse: any, outputUrl?: string) {
        const job = this.jobs.find(j => j.id === jobId);
        if (!job) return;
        job.response = { raw: rawResponse, outputUrl };
        job.status = 'success';
        job.endTime = Date.now();
        job.durationMs = job.endTime - job.startTime;
        this.log(jobId, 'RESPONSE', { status: 'success', outputUrl });
        this.log(jobId, 'JOB_END', { durationMs: job.durationMs });
        this.notify();
    }

    logError(jobId: string, error: any) {
        const job = this.jobs.find(j => j.id === jobId);
        if (!job) return;
        job.error = {
            message: error.message || String(error),
            stack: error.stack,
            code: error.status || 500
        };
        job.status = 'error';
        job.endTime = Date.now();
        job.durationMs = job.endTime - job.startTime;
        this.log(jobId, 'ERROR', { message: job.error.message });
        this.log(jobId, 'JOB_END', { durationMs: job.durationMs });
        this.notify();
    }

    private log(jobId: string, tag: string, data: any) {
        if (!this.isEnabled) return;

        // Console output structure requested
        console.log(`[FAL][${tag}]`, { jobId, ...data });

        const job = this.jobs.find(j => j.id === jobId);
        if (job) {
            job.logs.push({
                timestamp: Date.now(),
                tag: `[FAL][${tag}]`,
                data
            });
        }
    }
}

export const FalDebugService = new FalDebugServiceImpl();
